import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { FormField, CreateUserRequest, InitializeVerificationRequest, FinalizeVerificationRequest, ErrorMode } from '@/models';
import { makeFormField, stripLeadingZeros, getCountryByHostname } from '@/helpers';
import { CustomerError, CustomerErrorCode } from '@/services/customer/customer.service';
import { CountryCode } from '@/store/ui';
import { TwoStepVerificationEvent } from '@/services/analytics/events';
import { AnalyticsEventType } from '@/services/analytics/platforms/adobe-analytics/models';

export interface VerifyMobileNumberArgs {
  request: CreateUserRequest;
  country: string;
}

interface VerifyMobileNumberForm {
  [key: string]: FormField<string | boolean | null>;
  verificationCode: FormField;
  password: FormField;
}

const VERIFY_MOBILE_NUMBER_FORM: VerifyMobileNumberForm = {
  verificationCode: makeFormField(),
  password: makeFormField()
};

@Component({
  name: 'CmgVerifyMobileNumberModal',
})
export default class CmgVerifyMobileNumberModal extends Vue {
  isVerifying = false;
  isVerifyFormValid = true;
  isPasswordFormValid = true;
  isVerifyFlowError = false;
  isPasswordError = false;
  resendCodeCooldown = false;
  currentCountry = this.$appStore.state.ui.countryProfile.countryCode;
  verifyForm = VERIFY_MOBILE_NUMBER_FORM;
  verifyFlowErrorMessage = '';
  isLockedOut = false;
  cannotReceiveText = false;
  isVerifyingPhoneNumber = false;
  // a flag to toggle animation when swapping country
  showForm = true;

  @Prop({ default: true, type: Boolean })
  navigationAlert!: boolean;

  get modalImageSrc(): string {
    return this.currentCountry == CountryCode.US ? this.$appStore.state.cms.images.logos.medallionReward.default
      : this.$appStore.state.cms.images.logos.medallion.default;
  }

  get countryCode(): string {
    return this.currentCountry;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.verifyMobileNumber.visible;
  }

  get args() {
    return this.$appStore.state.ui.modals.verifyMobileNumber.args;
  }

  get form(): VerifyMobileNumberForm {
    return this.verifyForm;
  }

  get headerText(): string {
    return this.args?.requiresPasswordVerification ? 'Verify Your Password' : 'Verify Your Mobile Number';
  }

  get legalCopy(): string {
    return this.$services.config.getLegalText().createAnAccount2sv;
  }

  get maskedPhoneNumber(): string {
    const mobileNumberLastFour = this.args?.phoneNumber?.length == 4 ? this.args?.phoneNumber : this.args?.phoneNumber?.substring(this.args?.phoneNumber?.length - 4);
    return '***-***-' + mobileNumberLastFour;
  }

  get ctaText(): string {
    return this.isLockedOut ? 'Okay' : 'Verify';
  }

  get requiresPasswordVerification(): boolean | undefined {
    return this.args?.requiresPasswordVerification;
  }

  @Watch('visible', { immediate: true })
  async visibleChanged(newVisible: boolean) {
    if (newVisible) {
      if (!this.requiresPasswordVerification) {
        this.$services.analytics.send(new TwoStepVerificationEvent(AnalyticsEventType.TwoStepVerificationStart));
        this.initVerification();
      }
    } else {
      this.resetForm();
    }
  }

  private resetForm() {
    this.resendCodeCooldown = false;
    this.isLockedOut = false;
    this.isVerifyFormValid = true;
    this.isPasswordFormValid = true;
    this.verifyFlowErrorMessage = '';
    this.isPasswordError = false;
    this.verifyForm.verificationCode = makeFormField();
    this.verifyForm.password = makeFormField();
    this.isVerifyingPhoneNumber = false;
  }

  get siteCountryCode(): string {
    const countryCode = getCountryByHostname(window.location.hostname).countryCode;
    return countryCode;
  }

  get isUKCountry(): boolean {
    return this.siteCountryCode === CountryCode.GB;
  }

  onClose() {
    this.$services.ui.setLastSelectedElement('cmg-verify-mobile-number-modal');
    Vue.nextTick(() => {
      this.$services.ui.setModalVisibility('exitMobileVerificationModal', true, {
        onLeave: () => {
          if (this.args?.onCancel && !this.isLockedOut) {
            this.args.onCancel();
          } else {
            this.exitVerification();
          }
        }
      });
      this.$services.ui.focusDiv('.exit-mobile-verification-modal');
    });
  }

  async initVerification() {
    this.isVerifyingPhoneNumber = false;
    let phoneNumber2SV = this.args?.phoneNumber?.length && this.args?.phoneNumber?.length > 4 ? (this.siteCountryCode === CountryCode.GB ? `44${stripLeadingZeros(this.args?.phoneNumber)}` : this.args?.phoneNumber) : '';
    const initializeVerificationRequest: InitializeVerificationRequest = {
      email: this.args?.email || '',
      phoneNumber: phoneNumber2SV,
      password: this.form.password.value
    };

    try {
      this.isVerifying = true;
      await this.$services.customer.initializeVerification(initializeVerificationRequest);
      this.isVerifyingPhoneNumber = false;
      if (this.args?.requiresPasswordVerification) {
        this.isPasswordError = false;
        this.args.requiresPasswordVerification = false;
      }
    } catch (e: any) {
      switch (e.errorCode) {
        case CustomerErrorCode.TwoStepVerificaitonInvalidPassword:
          this.isVerifyingPhoneNumber = false;
          this.isPasswordError = true;
          break;
        case CustomerErrorCode.TwoStepVerificationInvalidPhoneNumber:
          this.isVerifyingPhoneNumber = false;
          // For the case when user just verified password but they entered an invalid phone number
          if (this.args?.requiresPasswordVerification) {
            this.args.requiresPasswordVerification = false;
          }
          // Do nothing else for invalid phone number. User will not receive verification code. Client disregards error.
          break;
        case CustomerErrorCode.AccountLocked:
          this.isVerifyingPhoneNumber = false;
          this.$services.ui.setModalVisibility('lockedOutModal', true);
          Vue.nextTick(() => {
            this.$services.ui.focusDiv('.cmg-locked-out-modal');
          });
          this.exitVerification();
          break;
        case CustomerErrorCode.PhoneInUse:
          this.isVerifyingPhoneNumber = false;
          this.$services.ui.setModalVisibility('twoStepVerificationWelcome', true, {
            email: this.args!.email, 
            phoneNumber: this.args!.oldPhoneNumber,
            oldPhoneNumber: this.args!.oldPhoneNumber, 
            requiresPasswordVerification: false,
            requiresSignOutOnFailure: true,
            onSuccess: this.args!.onSuccess,
            onFailure: this.args!.onFailure,//() => this.$services.ui.setModalVisibility('signIn', false)
            error: "CAU-0056"
          });
          this.$services.ui.setModalVisibility('verifyMobileNumber', false);
          break;
        default:
          this.isVerifyingPhoneNumber = false;
          this.$services.ui.setModalVisibility('verificationSystemErrorModal', true, {
            requiresSignOutOnFailure: this.args?.requiresSignOutOnFailure,
            onLeave: this.exitVerification,
          });
      }
    } finally {
      this.isVerifyingPhoneNumber = false;
      this.isVerifying = false;
    }
  }

  async exitVerification() {
    if (this.args?.requiresSignOutOnFailure) {
      this.args?.onFailure();
    } else if (this.args?.onCancel) {
      this.args.onCancel();
    }

    this.$services.ui.setModalVisibility('verifyMobileNumber', false);
    this.$services.ui.setModalVisibility('signIn', false);
  }

  async submit() {
    this.isVerifyingPhoneNumber = false;
    if (!this.isLockedOut) {

      if (this.requiresPasswordVerification) {
        this.validatePassword();
        if (!this.isPasswordFormValid) {
          return;
        }
        this.$services.analytics.send(new TwoStepVerificationEvent(AnalyticsEventType.TwoStepVerificationStart));
        this.initVerification();
      } else {
        const result = await this.verifyNumber();
        if (result) {
          this.isVerifying = true;
          try {
            await this.args?.onSuccess();
            this.$services.analytics.send(new TwoStepVerificationEvent(AnalyticsEventType.TwoStepVerificationComplete));
            if (this.$appStore.state.ui.selectedUpdateMobileNumberOnLogin) {
              this.$services.ui.setUpdatedMobileNumberOnLogin(false);
              this.$services.customer.updateCustomer({ phoneNumber: this.args?.phoneNumber });
              this.$services.ui.setModalVisibility('clearWalletModal', true);
            }
          } finally {
            this.isVerifying = false;
            this.$services.ui.setModalVisibility('verifyMobileNumber', false);
          }
        }
      }
    } else {
      this.args?.onFailure();
      this.$services.ui.setModalVisibility('verifyMobileNumber', false);
    }
  }

  async verifyNumber(): Promise<boolean> {
    this.isVerifying = true;
    this.isVerifyingPhoneNumber = false;
    this.validateVerifyForm();

    if (!this.isVerifyFormValid) {
      this.isVerifying = false;
      return false;
    }

    try {
      const finalizeVerificationRequest: FinalizeVerificationRequest = {
        email: this.args?.email || '',
        code: this.verifyForm.verificationCode.value
      };

      await this.$services.customer.finalizeVerification(finalizeVerificationRequest);
      this.isVerifying = false;
    } catch (error: any) {
      if (error instanceof CustomerError && (error.errorCode == CustomerErrorCode.InvalidVerificationCode || error.errorCode == CustomerErrorCode.ExpiredVerificationCode)
        && error.errorMaxTries && error.errorCount) {
        const verificationAttemptsLeft = error.errorMaxTries - error.errorCount;

        if (verificationAttemptsLeft > 1) {
          this.verifyForm.verificationCode.error = 'Please enter a valid verification code';
        } else if (verificationAttemptsLeft == 1) {
          this.verifyFlowErrorMessage = 'This is your last attempt. If unsuccessful, you can still order Chipotle as a guest.';
          this.isVerifyFlowError = true;
        } else {
          this.handleVerificationFailure();
        }
        this.$services.analytics.send(new TwoStepVerificationEvent(AnalyticsEventType.TwoStepVerificationInvalid));
      } else if (error instanceof CustomerError && error.errorCode == CustomerErrorCode.AccountLocked) {
        this.handleVerificationFailure();
        this.$services.analytics.send(new TwoStepVerificationEvent(AnalyticsEventType.TwoStepVerificationInvalidMax));
      } else {
        this.$services.ui.setModalVisibility('verificationSystemErrorModal', true, {
          requiresSignOutOnFailure: this.args?.requiresSignOutOnFailure,
          onLeave: () => this.exitVerification()
        });
      }
      this.isVerifying = false;
      return false;
    }

    return true;
  }

  handleVerificationFailure(): void {
    this.verifyFlowErrorMessage = 'Verification failed. You’ve used up all of your attempts. Try again in 30 minutes. Good thing you can still order Chipotle as a guest.'
    this.isVerifyFlowError = true;
    this.isLockedOut = true;
  }

  onResendCode() {
    if (!this.resendCodeCooldown) {
      this.$services.analytics.send(new TwoStepVerificationEvent(AnalyticsEventType.TwoStepVerificationResendCode));
      this.initVerification();
      this.resendCodeCooldown = true;
      this.$services.ui.setModalVisibility('toast', true, {
        title: 'Code Has Been Sent',
        body: 'Please wait 1 minute before trying again.'
      });

      setTimeout(() => { this.resendCodeCooldown = false; }, 60000);
    }
  }

  validateVerificationCode(): void {
    if (this.verifyForm.verificationCode.value.length == 0) {
      this.verifyForm.verificationCode.error = 'Verification Code Required';
    } else {
      this.verifyForm.verificationCode.error = '';
    }

    this.verifyForm.verificationCode.errorMode = ErrorMode.BelowField;
  }

  validatePassword(): void {
    this.isPasswordFormValid = true;
    if (this.verifyForm.password.value.length == 0) {
      this.verifyForm.password.error = 'Password Required';
      this.isPasswordFormValid = false;
    } else {
      this.verifyForm.password.error = '';
    }

    this.verifyForm.password.errorMode = ErrorMode.BelowField;
  }

  validateVerifyForm() {
    this.verifyFlowErrorMessage = '';
    this.isVerifyFormValid = true;

    this.validateVerificationCode();

    for (const key of Object.keys(this.verifyForm)) {
      if (!!this.verifyForm[key].error) {
        this.verifyForm[key].dirty = true;
        this.isVerifyFormValid = false;
      }
    }
  }

  toggleCannotReceiveText(): void {
    this.cannotReceiveText = !this.cannotReceiveText;
  }

  onClickContactCustomerCare() {
    this.$services.analytics.send(new TwoStepVerificationEvent(AnalyticsEventType.TwoStepVerificationGetSupport));
  }
}
